import React, { useContext } from 'react';
import { Formik } from 'formik';
import { ThemeProvider } from 'emotion-theming';
import debounce from 'lodash/debounce';
import { UserFlags } from '@ordermentum/ui-types';
import { Theme, ThemeContext } from '../../../utilities/theme';
import { ColorCodes, colorsForTheme } from '../../../foundations/color';
import { Input } from '../../../atoms/Input';
import Button from '../../Button/Button';
import {
  creditCardNumberModifier,
  cvvModifier,
  expiryDateModifier,
  getCCExpiryYear,
} from '../../../utilities';
import { paymentFormSchema } from '../../../utilities/formSchemas';
import {
  FormContainer,
  InputWrapper,
  StickyFooter,
  FooterButtonWrappers,
  FooterContent,
  RightContainer,
  TwoColumnInputWrapper,
  Footer,
} from './Form.styles';
import PurchaserToggleBlock from './PurchaserToggleBlock';
import { ValidateCardParams, CardParams } from '../../../redux/payment_methods';

type StateProps = {
  params: { retailerId: string };
  purchaserId?: string;
  addCardPayment: (cardDetails: CardParams, retailerId: string) => void;
  fetchPayments?: (retailerId: string, flags: UserFlags) => void;
  getCardType: (cardNumber: string) => void;
  validateCard: (cardDetails: ValidateCardParams) => void;
  onSuccess?: (paymentMethodId: string) => void;
  onClose?: () => void;
  flags: UserFlags;
  isStickyFooter?: boolean;
  hasDefaultForAllToggle?: boolean;
  hideCancel?: boolean;
};

type themeStyles = {
  backgroundColor: ColorCodes;
  headerTitleColor: ColorCodes;
  textTitleColor: ColorCodes;
  subTextColor: ColorCodes;
  dividerColor: ColorCodes;
  iconColor: ColorCodes;
  descriptionTitleColor: ColorCodes;
  linkTextColor: ColorCodes;
  activeBackgroundColor: ColorCodes;
};

const getTheme = (theme: Theme): themeStyles => {
  const colors = colorsForTheme(theme);
  return {
    backgroundColor: colors.SWAN,
    headerTitleColor: colors.SPACE,
    textTitleColor: colors.SPACE,
    subTextColor: colors.STEEL,
    dividerColor: colors.ASH,
    iconColor: colors.SWEET_ORANGE,
    descriptionTitleColor: colors.STORM,
    linkTextColor: colors.SWEET_ORANGE,
    activeBackgroundColor: colors.LIGHT_CREAM,
  };
};

export const AddCardFormComponent = (pageProps: StateProps) => {
  const {
    params,
    purchaserId,
    addCardPayment,
    flags,
    fetchPayments,
    getCardType,
    validateCard,
    onSuccess = () => {},
    onClose,
    isStickyFooter = false,
    hasDefaultForAllToggle = false,
    hideCancel = false,
  } = pageProps;
  const themeContext = useContext(ThemeContext);
  const { retailerId } = params;
  const theme = getTheme(themeContext);

  const onSubmitFunction = async (values, actions) => {
    try {
      const payload = {
        cvv: values.cvv,
        expiryMonth: values.expiryDate.split('/')[0],
        expiryYear: getCCExpiryYear(values.expiryDate),
        issuer:
          values.cardType === 'americanexpress' ? 'amex' : values.cardType,
        name: values.cardName,
        number: values.cardNumber,
        defaultPayment: true,
        defaultAll: values.defaultAll,
        userId: retailerId,
        purchaserId,
      };

      const validateRes: any = await validateCard({
        ...payload,
        type: values.cardType === 'americanexpress' ? 'ae' : values.cardType,
        expireMonth: payload.expiryMonth,
        expireYear: payload.expiryYear,
      });

      const validity = validateRes?.value?.data;
      if (validity) {
        if (validity.isExpired) {
          actions.setFieldError('expiryDate', 'Card is expired');
        }
        if (!validity.validCardNumber) {
          actions.setFieldError('cardNumber', 'Card number is not valid');
        }
        if (!validity.validCvv) {
          actions.setFieldError('cvv', 'CVV is not valid');
        }
        if (!validity.validExpiryMonth || !validity.validExpiryYear) {
          actions.setFieldError('expiryDate', 'Expiry date is not valid');
        }
        if (
          validity.isExpired ||
          !validity.validCardNumber ||
          !validity.validCvv ||
          !validity.validExpiryMonth ||
          !validity.validExpiryYear
        ) {
          actions.setSubmitting(false);
          return;
        }
      }

      const res: any = await addCardPayment(payload, retailerId);

      if (fetchPayments) {
        await fetchPayments(retailerId, flags);
      }

      actions.resetForm();
      onSuccess(res.value.data.id);
    } catch (ex) {
      actions.setSubmitting(false);
    }
  };

  const identifyCardType = debounce(async (val, setValue) => {
    const cardTypeResult: any = await getCardType(val);
    setValue('cardType', cardTypeResult?.value?.data?.toLowerCase());
  }, 500);

  return (
    <ThemeProvider theme={theme}>
      <Formik
        validationSchema={paymentFormSchema}
        validateOnChange={false}
        validateOnBlur={false}
        enableReinitialize={true}
        initialValues={{
          cardName: '',
          cardNumber: '',
          expiryDate: '',
          cvv: '',
          cardType: '',
          readTerms: false,
          defaultAll: false,
        }}
        onSubmit={(values, actions) => {
          onSubmitFunction(values, actions);
        }}
      >
        {({
          dirty,
          handleSubmit,
          values,
          errors,
          setFieldValue,
          isSubmitting,
          resetForm,
        }) => (
          <form onSubmit={handleSubmit}>
            <RightContainer
              hasChanges={dirty}
              isStickyFooter={isStickyFooter}
              data-testid="payment-methods-add-card"
            >
              <FormContainer>
                <InputWrapper>
                  <Input
                    tag={'card-name'}
                    inputLabel="Name on card"
                    theme={themeContext}
                    hasError={!!errors.cardName}
                    errorLabel={errors.cardName}
                    size="max"
                    value={values.cardName}
                    onChange={(e, val) => {
                      setFieldValue('cardName', val);
                    }}
                    onBlur={(e, val) => {
                      setFieldValue('cardName', val);
                    }}
                  />
                </InputWrapper>
                <InputWrapper>
                  <Input
                    tag={'card-number'}
                    inputLabel="Card number"
                    theme={themeContext}
                    hasError={!!errors.cardNumber}
                    errorLabel={errors.cardNumber}
                    size="max"
                    value={values.cardNumber}
                    image={`${process.env.IMAGE_URL}/${values.cardType}.svg`}
                    onChange={(e, val) => {
                      setFieldValue(
                        'cardNumber',
                        creditCardNumberModifier(val)
                      );
                      identifyCardType(val, setFieldValue);
                    }}
                    onBlur={(e, val) => {
                      setFieldValue(
                        'cardNumber',
                        creditCardNumberModifier(val)
                      );
                      identifyCardType(val, setFieldValue);
                    }}
                  />
                </InputWrapper>
                <TwoColumnInputWrapper>
                  <InputWrapper>
                    <Input
                      tag={'card-expiry'}
                      placeholder="MM/YY"
                      inputLabel="Expiry date"
                      theme={themeContext}
                      hasError={!!errors.expiryDate}
                      errorLabel={errors.expiryDate}
                      size="max"
                      value={values.expiryDate}
                      onChange={(e, val) => {
                        const newValue = expiryDateModifier(val);
                        setFieldValue('expiryDate', newValue);
                      }}
                      onBlur={(e, val) => {
                        const newValue = expiryDateModifier(val);
                        setFieldValue('expiryDate', newValue);
                      }}
                    />
                  </InputWrapper>
                  <InputWrapper>
                    <Input
                      tag={'card-cvv'}
                      inputLabel="CVV"
                      theme={themeContext}
                      size="max"
                      value={values.cvv}
                      hasError={!!errors.cvv}
                      errorLabel={errors.cvv}
                      onChange={(e, val) => {
                        setFieldValue('cvv', cvvModifier(val));
                      }}
                      onBlur={(e, val) => {
                        setFieldValue('cvv', cvvModifier(val));
                      }}
                    />
                  </InputWrapper>
                </TwoColumnInputWrapper>
              </FormContainer>
              {hasDefaultForAllToggle && (
                <PurchaserToggleBlock
                  type={'card'}
                  allowToggleAll
                  formikFunction={checked => {
                    setFieldValue('defaultAll', checked);
                  }}
                  formikValue={values.defaultAll}
                />
              )}
            </RightContainer>
            {isStickyFooter ? (
              <StickyFooter hasChanges={dirty}>
                <FooterContent>
                  <FooterButtonWrappers>
                    <Button
                      variant="secondary"
                      type="reset"
                      full
                      onClick={() => resetForm()}
                    >
                      Cancel
                    </Button>
                  </FooterButtonWrappers>
                  <FooterButtonWrappers>
                    <Button
                      variant="primary"
                      type="submit"
                      full
                      loading={isSubmitting}
                    >
                      Save payment method
                    </Button>
                  </FooterButtonWrappers>
                </FooterContent>
              </StickyFooter>
            ) : (
              <Footer>
                {!hideCancel && (
                  <Button
                    variant="tertiary"
                    type="reset"
                    onClick={() => {
                      resetForm();
                      if (onClose) {
                        onClose();
                      }
                    }}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  data-testid="save-credit-debit-button"
                  variant="primary"
                  type="submit"
                  full={hideCancel}
                  disabled={isSubmitting}
                >
                  Save
                </Button>
              </Footer>
            )}
          </form>
        )}
      </Formik>
    </ThemeProvider>
  );
};

export default AddCardFormComponent;
